/**
 * Read-only view of a group template's content for the selected-template card:
 * the group header line, then the items table; each item expands into its
 * subitems. Same table component as everywhere else, so cells, chips and update
 * counts look exactly as they do in the editor.
 */
import React, { useState } from 'react';
import { TokenText } from '../DynamicTextField';
import SubitemsTable from '../SubitemsTable';
import { DEFAULT_GROUP_COLOR } from '../SubitemsTable/SubitemsTable';
import { ITEM_LABELS, subitemsBadge } from './labels';

export default function GroupTemplateView({ content }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const c = content || {};
  const items = c.items || [];
  const color = (c.group && c.group.color) || DEFAULT_GROUP_COLOR;
  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-lg font-semibold" style={{ color: color }}>
          {c.group && c.group.title ? <TokenText value={c.group.title} /> : 'Untitled group'}
        </span>
        <span className="text-sm text-secondary">
          {items.length} item{items.length === 1 ? '' : 's'}
        </span>
      </div>
      <SubitemsTable
        groupColor={color}
        columns={c.item_columns || []}
        subitems={items.map((it) => ({ id: it.key, name: it.name, column_values: it.column_values || [], updates: it.updates || [], subitems: it.subitems || [] }))}
        readOnly
        labels={ITEM_LABELS}
        subitemBadge={subitemsBadge}
        expandedIds={expanded}
        onToggleExpand={toggle}
        renderExpanded={(row) => {
          const it = items.find((x) => x.key === row.id);
          const subs = (it && it.subitems) || [];
          return subs.length ? (
            <SubitemsTable
              columns={c.subitem_columns || []}
              subitems={subs.map((s) => ({ id: s.key, name: s.name, column_values: s.column_values || [], updates: s.updates || [] }))}
              readOnly
              groupColor={color}
              nested
            />
          ) : (
            <span className="text-sm text-secondary">No subitems on this item.</span>
          );
        }}
      />
    </div>
  );
}
